import React, { useState, useMemo, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Tabs,
  Tab,
  Divider,
  Grid,
  TextField,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Button,
  Stack,
  Chip,
  LinearProgress,
  Alert,
  IconButton,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  CircularProgress,
} from '@mui/material';
import {
  Assessment as ReportsIcon,
  History as HistoryIcon,
  PictureAsPdf as PdfIcon,
  TableChart as CsvIcon,
  Article as WordIcon,
  AutoAwesome as GenerateIcon,
  Save as SaveIcon,
  DeleteOutline as DeleteIcon,
  PlayArrow as ApplyIcon,
  Bolt as QuickIcon,
  OpenInNew as OpenInNewIcon,
} from '@mui/icons-material';
import { useAuth } from '../../../contexts/AuthContext';
import { useDemoRole } from '../../../contexts/DemoRoleContext';
import { useUserOrganizations } from '../hooks/useOrganization';
import { useSiteComparisons } from '../hooks/useSiteComparisons';
import { useOEScores } from '../hooks/useOEScores';
import WarningHistory from '../components/WarningHistory';
import ScopeSelector from '../components/ScopeSelector';
import { REPS_COLORS, oeBand } from '../utils/constants';
import {
  REPORT_PERIODS,
  periodCutoff,
  buildScopeOptions,
  assembleReport,
  REPORT_PRESETS,
  CASE_STUDY_OPTION_DEFAULTS,
  CASE_STUDY_SOURCES,
  groupCaseStudiesByCategory,
} from '../utils/reportData';
import { getVisibleCaseStudies } from '../../../services/documentService';
import BenchmarkDocumentPicker from '../components/BenchmarkDocumentPicker';
import { CONDITION_LOG_COLUMNS, conditionLogRow } from '../utils/reportRows';
import { conditionStatusColor } from '../utils/trlConstants';
import { exportReportPDF, exportReportCSV, exportReportDoc } from '../utils/reportExport';

const QUADS = ['reliability', 'efficiency', 'productivity', 'safety'];
const cap = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);
const TPL_KEY = 'hugo_report_templates';

const SECTION_DEFS = [
  { key: 'performance', label: 'REPS Performance', desc: 'Quadrant scores + warning bands' },
  { key: 'narrative', label: 'Executive Briefing', desc: 'Written summary + recommendations' },
  { key: 'trls', label: 'OE Condition & REF Action Log', desc: 'Logged OE Conditions + REF actions (live data)' },
  { key: 'caseStudies', label: 'Case Study Benchmarks', desc: 'Completed analyses to compare against' },
];

function TabPanel({ children, value, index }) {
  return value === index ? <Box sx={{ pt: 3 }}>{children}</Box> : null;
}

// ---- Live preview of the assembled report ----
function ReportPreview({ report, generating }) {
  if (generating) {
    return (
      <Box sx={{ py: 6, textAlign: 'center' }}>
        <LinearProgress sx={{ mb: 2 }} />
        <Typography variant="body2" color="text.secondary">Assembling your report…</Typography>
      </Box>
    );
  }
  if (!report) {
    return (
      <Box sx={{ py: 8, textAlign: 'center' }}>
        <ReportsIcon sx={{ fontSize: 44, color: 'text.disabled', mb: 1 }} />
        <Typography variant="body1" color="text.secondary">Choose your sections, scope, and period, then <strong>Generate</strong>.</Typography>
        <Typography variant="caption" color="text.secondary">A live preview appears here, and you can download it as PDF, CSV, or Word.</Typography>
      </Box>
    );
  }
  const s = report.sections;
  return (
    <Box>
      <Typography variant="h5" fontWeight={700} sx={{ color: '#1F3864' }}>{report.title}</Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
        {report.scopeLabel} • {report.periodLabel} • Generated {report.generatedAt} • {report.personaLabel}
      </Typography>
      <Divider sx={{ my: 2 }} />

      {s.performance.include && s.performance.scores && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" fontWeight={700} sx={{ color: '#2E75B6', mb: 1 }}>REPS Performance</Typography>
          <Stack spacing={1}>
            {QUADS.map((q) => {
              const v = s.performance.scores[q];
              if (v == null) return null;
              const band = oeBand(v, s.performance.thresholds?.[q] ?? 6);
              return (
                <Box key={q} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2" sx={{ color: REPS_COLORS[q], fontWeight: 700, width: 120 }}>{cap(q)}</Typography>
                  <Typography variant="body2" sx={{ color: band.color, fontWeight: 700, width: 40, textAlign: 'right' }}>{v.toFixed(1)}</Typography>
                  <Typography variant="body2" sx={{ color: '#1F3864', fontWeight: 700, width: 36 }}>/ 10</Typography>
                  <Chip label={band.label} size="small" sx={{ bgcolor: band.color, color: '#fff', fontWeight: 700 }} />
                </Box>
              );
            })}
          </Stack>
          {s.performance.trend && <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>Trend: {s.performance.trend}</Typography>}
        </Box>
      )}

      {s.narrative.include && s.narrative.text && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" fontWeight={700} sx={{ color: '#2E75B6', mb: 1 }}>Executive Briefing</Typography>
          {s.narrative.text.split('\n\n').map((p, i) => (
            <Typography key={i} variant="body2" sx={{ mb: 1 }}>{p}</Typography>
          ))}
        </Box>
      )}

      {s.trls.include && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" fontWeight={700} sx={{ color: '#2E75B6', mb: 1 }}>OE Condition &amp; REF Action Log ({s.trls.items.length})</Typography>
          {s.trls.items.length === 0 ? (
            <Typography variant="body2" color="text.secondary">No OE Conditions logged in this period for this scope.</Typography>
          ) : (
            <Box sx={{ overflowX: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>{CONDITION_LOG_COLUMNS.map(([key, label]) => <TableCell key={key} sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>{label}</TableCell>)}</TableRow>
                </TableHead>
                <TableBody>
                  {s.trls.items.map((t) => {
                    const r = conditionLogRow(t, report.org);
                    return (
                      <TableRow key={t.id}>
                        {CONDITION_LOG_COLUMNS.map(([key]) => (
                          <TableCell key={key} sx={{ verticalAlign: 'top' }}>
                            {key === 'status' ? <Chip label={r.status} size="small" sx={{ bgcolor: conditionStatusColor(t.status), color: '#fff', fontWeight: 700 }} /> : key === 'id' ? <Typography variant="caption" sx={{ fontFamily: '"Roboto Mono", monospace' }}>{r.id}</Typography> : <Typography variant="body2">{r[key]}</Typography>}
                          </TableCell>
                        ))}
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Box>
          )}
        </Box>
      )}

      {s.caseStudies.include && (
        <Box>
          <Typography variant="subtitle1" fontWeight={700} sx={{ color: '#2E75B6', mb: 0.5 }}>Case Study Benchmarks ({s.caseStudies.items.length})</Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            {s.caseStudies.picked ? 'Your scope compared against the documents you picked.' : 'Your scope compared against the closest library documents.'} Document numbers are REPS impact (0 = none, 10 = critical); yours are OE scores. Higher is worse on both — a green arrow means your scope sits below the document.
          </Typography>
          {s.caseStudies.items.length === 0 ? (
            <Typography variant="body2" color="text.secondary">No documents matched — pick documents in the builder or widen the filters.</Typography>
          ) : (
            <Box sx={{ overflowX: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>Document</TableCell>
                    {QUADS.map((q) => <TableCell key={q} align="center" sx={{ fontWeight: 700, color: REPS_COLORS[q] }}>{cap(q)}</TableCell>)}
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ color: 'text.secondary' }}>Your scope</TableCell>
                    {QUADS.map((q) => <TableCell key={q} align="center" sx={{ fontWeight: 700 }}>{s.caseStudies.scopeScores?.[q] != null ? Number(s.caseStudies.scopeScores[q]).toFixed(1) : '—'}</TableCell>)}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {s.caseStudies.items.map((c, i) => (
                    <TableRow key={c.id || i}>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>{c.title}</Typography>
                        <Typography variant="caption" color="text.secondary">{[c.categoryLabel, c.industry, c.source === 'org' ? 'my organization' : null].filter(Boolean).join(' • ')}</Typography>
                      </TableCell>
                      {QUADS.map((q) => {
                        const v = c.repsScores?.[q]; const d = c.delta?.[q];
                        return (
                          <TableCell key={q} align="center">
                            <Typography variant="body2">{v != null ? Number(v).toFixed(1) : '—'}</Typography>
                            {d != null && (
                              <Typography variant="caption" sx={{ fontWeight: 700, color: d < 0 ? 'success.main' : d > 0 ? 'warning.dark' : 'text.secondary' }}>
                                {d < 0 ? '▼' : d > 0 ? '▲' : '='} {Math.abs(d).toFixed(1)}
                              </Typography>
                            )}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
}

function REFReports() {
  const { user } = useAuth();
  const { persona } = useDemoRole();
  const { organizations, loading: orgLoading } = useUserOrganizations(user?.uid);
  const organization = organizations.length > 0 ? organizations[0] : null;
  const orgId = organization?.id || null;
  // P5-05: Warning History has its own scope + period selectors (Tim's org showed "none" because
  // the org-level view was selected while the warnings sat on Houston's site snapshots).
  const [whScope, setWhScope] = useState({ type: 'organization', id: null });
  const [whPeriod, setWhPeriod] = useState('last365');
  const { snapshots: whSnapshotsAll, loading: scoresLoading } = useOEScores(orgId, whScope);
  const whSnapshots = useMemo(() => {
    const from = periodCutoff(whPeriod);
    if (!from) return whSnapshotsAll;
    return whSnapshotsAll.filter((sn) => {
      const t = sn.calculatedAt?.toMillis ? sn.calculatedAt.toMillis() : sn.calculatedAt?.seconds ? sn.calculatedAt.seconds * 1000 : 0;
      return t >= from.getTime();
    });
  }, [whSnapshotsAll, whPeriod]);
  const whScopeLabel = useMemo(() => {
    if (!organization || !whScope || whScope.type === 'organization') return 'All sites';
    const list = whScope.type === 'site' ? organization.sites : whScope.type === 'department' ? organization.departments : organization.teams;
    return (list || []).find((u) => u.id === whScope.id)?.name || whScope.type;
  }, [organization, whScope]);
  const { comparisons } = useSiteComparisons(orgId);

  const [tab, setTab] = useState(0);

  // Builder state
  const { queryScope } = useDemoRole();
  const scopeOptions = useMemo(() => buildScopeOptions(comparisons, queryScope), [comparisons, queryScope]);
  const [scopeValue, setScopeValue] = useState('enterprise');
  // Scoped members can't report on 'enterprise' — snap to their first allowed scope.
  useEffect(() => {
    if (scopeOptions.length && !scopeOptions.some((o) => o.value === scopeValue)) {
      setScopeValue(scopeOptions[0].value);
    }
  }, [scopeOptions, scopeValue]);
  const [period, setPeriod] = useState('last90');
  const [range, setRange] = useState({ from: '', to: '' }); // P5-03 Specific period
  const [csOptions, setCsOptions] = useState({ ...CASE_STUDY_OPTION_DEFAULTS }); // P5-04 (Q-03)
  const [title, setTitle] = useState('');
  const [sel, setSel] = useState({ performance: true, narrative: true, trls: true, caseStudies: false });
  // Q-03 (Tim, Aug 25): pick specific library documents, grouped by category, to benchmark against.
  const [libraryDocs, setLibraryDocs] = useState(null);
  useEffect(() => {
    if (!sel.caseStudies || libraryDocs !== null) return;
    let alive = true;
    getVisibleCaseStudies(orgId || null).then((list) => { if (alive) setLibraryDocs(list.filter((c) => ['ready', 'published'].includes(c.status) || !c.status)); }).catch(() => { if (alive) setLibraryDocs([]); });
    return () => { alive = false; };
  }, [sel.caseStudies, orgId, libraryDocs]);
  const libraryGroups = useMemo(() => groupCaseStudiesByCategory(libraryDocs || []), [libraryDocs]);
  const pickedCount = (csOptions.documentIds || []).length;
  const [report, setReport] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState(null);

  const [templates, setTemplates] = useState(() => {
    try { return JSON.parse(localStorage.getItem(TPL_KEY)) || []; } catch { return []; }
  });

  const anySelected = Object.values(sel).some(Boolean);
  const toggle = (k) => setSel((s) => ({ ...s, [k]: !s[k] }));

  const [quickBusy, setQuickBusy] = useState(null); // `${presetId}:${format}`

  const handleGenerate = async () => {
    setGenerating(true);
    setError(null);
    try {
      const model = await assembleReport({ sel, scopeValue, period, range, title, personaLabel: `Prepared as ${persona.label}`, comparisons, organizationId: orgId, thresholds: organization?.thresholds, caseStudyOptions: csOptions, org: organization });
      setReport(model);
    } catch (e) {
      console.error('Report generation failed:', e);
      setError(e.message || 'Could not generate the report.');
    } finally {
      setGenerating(false);
    }
  };

  // One-click: assemble a preset and immediately export in the chosen format.
  const quickExport = async (preset, format) => {
    setQuickBusy(`${preset.id}:${format}`);
    setError(null);
    try {
      const model = await assembleReport({ ...preset.config, personaLabel: `Prepared as ${persona.label}`, comparisons, organizationId: orgId, thresholds: organization?.thresholds, org: organization });
      if (format === 'pdf') exportReportPDF(model);
      else if (format === 'csv') exportReportCSV(model);
      else exportReportDoc(model);
    } catch (e) {
      console.error('Quick export failed:', e);
      setError(e.message || 'Could not export this report.');
    } finally {
      setQuickBusy(null);
    }
  };

  // Load a preset into the builder, switch tabs, and generate the preview.
  const openInBuilder = async (preset) => {
    setSel(preset.config.sel);
    setScopeValue(preset.config.scopeValue);
    setPeriod(preset.config.period);
    setTitle(preset.config.title || '');
    setTab(1);
    setGenerating(true);
    setError(null);
    try {
      const model = await assembleReport({ ...preset.config, personaLabel: `Prepared as ${persona.label}`, comparisons, organizationId: orgId, thresholds: organization?.thresholds, org: organization });
      setReport(model);
    } catch (e) {
      setError(e.message || 'Could not generate the report.');
    } finally {
      setGenerating(false);
    }
  };

  const saveTemplate = () => {
    const name = window.prompt('Save this configuration as a template. Name:');
    if (!name) return;
    const cfg = { name, sel, scopeValue, period, range, csOptions, title };
    const next = [...templates.filter((t) => t.name !== name), cfg];
    setTemplates(next);
    try { localStorage.setItem(TPL_KEY, JSON.stringify(next)); } catch { /* ignore */ }
  };
  const applyTemplate = (t) => {
    setSel(t.sel); setScopeValue(t.scopeValue); setPeriod(t.period); setRange(t.range || { from: '', to: '' }); setCsOptions({ ...CASE_STUDY_OPTION_DEFAULTS, ...(t.csOptions || {}) }); setTitle(t.title || ''); setReport(null);
  };
  const deleteTemplate = (name) => {
    const next = templates.filter((t) => t.name !== name);
    setTemplates(next);
    try { localStorage.setItem(TPL_KEY, JSON.stringify(next)); } catch { /* ignore */ }
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>Reports</Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
        Build, preview, and download custom reports: REPS Performance, The OE Condition/REF Action Log, Executive Briefings, and Case Study Benchmarks
      </Typography>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 1 }}>
        <Tab icon={<QuickIcon />} iconPosition="start" label="Quick Reports" />
        <Tab icon={<ReportsIcon />} iconPosition="start" label="Report Builder" />
        <Tab icon={<HistoryIcon />} iconPosition="start" label="Warning History" />
      </Tabs>
      <Divider />

      {/* ---- Quick Reports (one-click presets) ---- */}
      <TabPanel value={tab} index={0}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Ready-made analytics — generate and download in one click, no building required. Need something specific? Open any of these in the builder to tweak it.
        </Typography>

        <Grid container spacing={2}>
          {REPORT_PRESETS.filter((p) => !p.builderOnly).map((preset) => (
            <Grid item xs={12} md={6} key={preset.id}>
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  <Typography variant="h6" fontWeight={700}>{preset.title}</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1, minHeight: 40 }}>{preset.desc}</Typography>
                  <Stack direction="row" spacing={0.5} sx={{ mb: 1.5, flexWrap: 'wrap' }} useFlexGap>
                    {preset.includes.map((i) => <Chip key={i} label={i} size="small" variant="outlined" />)}
                  </Stack>
                  <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap' }} useFlexGap>
                    {[['pdf', 'PDF', <PdfIcon key="p" />], ['csv', 'CSV', <CsvIcon key="c" />], ['doc', 'Word', <WordIcon key="w" />]].map(([fmt, lbl, ic]) => {
                      const busy = quickBusy === `${preset.id}:${fmt}`;
                      return (
                        <Button
                          key={fmt} size="small" variant="outlined"
                          startIcon={busy ? <CircularProgress size={14} /> : ic}
                          disabled={!!quickBusy}
                          onClick={() => quickExport(preset, fmt)}
                        >
                          {lbl}
                        </Button>
                      );
                    })}
                    <Button size="small" startIcon={<OpenInNewIcon />} onClick={() => openInBuilder(preset)}>Open in builder</Button>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
        {error && <Alert severity="error" sx={{ mt: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      </TabPanel>

      {/* ---- Report Builder ---- */}
      <TabPanel value={tab} index={1}>
        <Grid container spacing={3}>
          {/* Builder controls */}
          <Grid item xs={12} md={4}>
            <Card sx={{ mb: 2 }}>
              <CardContent>
                <Typography variant="overline" color="text.secondary">Report Builder</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>Customize, preview, and download specific reports for specific periods</Typography>
                <TextField
                  fullWidth size="small" label="Report title (optional)" sx={{ mt: 1, mb: 2 }}
                  placeholder="Auto-named from scope if left blank"
                  value={title} onChange={(e) => setTitle(e.target.value)}
                />
                <TextField select fullWidth size="small" label="Scope" value={scopeValue} onChange={(e) => setScopeValue(e.target.value)} sx={{ mb: 2 }}>
                  {scopeOptions.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
                </TextField>
                <TextField select fullWidth size="small" label="Time period" value={period} onChange={(e) => setPeriod(e.target.value)} sx={{ mb: period === 'custom' ? 1 : 2 }}>
                  {REPORT_PERIODS.map((p) => <MenuItem key={p.value} value={p.value}>{p.label}</MenuItem>)}
                </TextField>
                {period === 'custom' && (
                  <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                    <TextField type="date" size="small" label="From" value={range.from} onChange={(e) => setRange((r) => ({ ...r, from: e.target.value }))} InputLabelProps={{ shrink: true }} fullWidth inputProps={{ 'aria-label': 'Period from' }} />
                    <TextField type="date" size="small" label="To" value={range.to} onChange={(e) => setRange((r) => ({ ...r, to: e.target.value }))} InputLabelProps={{ shrink: true }} fullWidth inputProps={{ 'aria-label': 'Period to' }} />
                  </Stack>
                )}

                <Typography variant="overline" color="text.secondary">Sections</Typography>
                <Box sx={{ mb: 1 }}>
                  {SECTION_DEFS.map((d) => (
                    <Box key={d.key}>
                      <FormControlLabel
                        control={<Checkbox size="small" checked={sel[d.key]} onChange={() => toggle(d.key)} />}
                        label={<Box><Typography variant="body2">{d.label}</Typography><Typography variant="caption" color="text.secondary">{d.desc}</Typography></Box>}
                        sx={{ alignItems: 'flex-start', display: 'flex', mb: 0.5 }}
                      />
                      {d.key === 'caseStudies' && sel.caseStudies && (
                        <Box sx={{ ml: 4, mb: 1, p: 1.25, bgcolor: 'action.hover', borderRadius: 1 }}>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>Which documents to benchmark against</Typography>
                          <BenchmarkDocumentPicker
                            groups={libraryGroups}
                            loading={libraryDocs === null}
                            selected={csOptions.documentIds || []}
                            onChange={(ids) => setCsOptions((o) => ({ ...o, documentIds: ids }))}
                          />
                          {pickedCount === 0 && (
                            <Box sx={{ mt: 1 }}>
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>No documents picked — the report chooses by these filters instead:</Typography>
                              <TextField select fullWidth size="small" label="Source" value={csOptions.source} onChange={(e) => setCsOptions((o) => ({ ...o, source: e.target.value }))} sx={{ mb: 1 }}>
                                {CASE_STUDY_SOURCES.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
                              </TextField>
                              <TextField fullWidth size="small" label="Industry contains" value={csOptions.industry} onChange={(e) => setCsOptions((o) => ({ ...o, industry: e.target.value }))} placeholder="e.g. chemical, refining" sx={{ mb: 1 }} />
                              <TextField select fullWidth size="small" label="Focus quadrant" value={csOptions.quadrant} onChange={(e) => setCsOptions((o) => ({ ...o, quadrant: e.target.value }))} sx={{ mb: 1 }}>
                                <MenuItem value="">All quadrants</MenuItem>
                                {QUADS.map((q) => <MenuItem key={q} value={q}>{cap(q)}</MenuItem>)}
                              </TextField>
                              <TextField type="number" fullWidth size="small" label="Max case studies" value={csOptions.limit} onChange={(e) => setCsOptions((o) => ({ ...o, limit: e.target.value }))} inputProps={{ min: 1, max: 20 }} />
                            </Box>
                          )}
                        </Box>
                      )}
                    </Box>
                  ))}
                </Box>

                {error && <Alert severity="error" sx={{ mb: 1 }} onClose={() => setError(null)}>{error}</Alert>}

                <Button
                  fullWidth variant="contained" startIcon={<GenerateIcon />}
                  disabled={!anySelected || generating}
                  onClick={handleGenerate}
                >
                  {generating ? 'Generating…' : 'Generate'}
                </Button>

                <Divider sx={{ my: 2 }} />
                <Typography variant="overline" color="text.secondary">Download</Typography>
                <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                  <Button size="small" variant="outlined" startIcon={<PdfIcon />} disabled={!report} onClick={() => exportReportPDF(report)}>PDF</Button>
                  <Button size="small" variant="outlined" startIcon={<CsvIcon />} disabled={!report} onClick={() => exportReportCSV(report)}>CSV</Button>
                  <Button size="small" variant="outlined" startIcon={<WordIcon />} disabled={!report} onClick={() => exportReportDoc(report)}>Word</Button>
                </Stack>
              </CardContent>
            </Card>

            {/* Templates */}
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Typography variant="overline" color="text.secondary">Saved templates</Typography>
                  <Button size="small" startIcon={<SaveIcon />} onClick={saveTemplate}>Save current</Button>
                </Box>
                {templates.length === 0 ? (
                  <Typography variant="caption" color="text.secondary">No templates yet. Configure a report and click “Save current” to reuse it later.</Typography>
                ) : (
                  <Stack spacing={0.5} sx={{ mt: 1 }}>
                    {templates.map((t) => (
                      <Box key={t.name} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                        <Typography variant="body2" sx={{ flex: 1, minWidth: 0 }} noWrap>{t.name}</Typography>
                        <Tooltip title="Apply"><IconButton size="small" onClick={() => applyTemplate(t)}><ApplyIcon fontSize="small" /></IconButton></Tooltip>
                        <Tooltip title="Delete"><IconButton size="small" onClick={() => deleteTemplate(t.name)}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                      </Box>
                    ))}
                  </Stack>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Preview */}
          <Grid item xs={12} md={8}>
            <Card sx={{ minHeight: 420 }}>
              <CardContent>
                <ReportPreview report={report} generating={generating} />
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </TabPanel>

      {/* ---- Warning History ---- */}
      <TabPanel value={tab} index={2}>
        {(orgLoading || scoresLoading) ? (
          <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>Loading warning history…</Typography>
        ) : !organization ? (
          <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>No organization found. Create or join an organization to view warning history.</Typography>
        ) : (
          <Box>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }} sx={{ mb: 2 }}>
              <ScopeSelector value={whScope} onChange={setWhScope} organization={organization} lockScope={queryScope} />
              <TextField select size="small" label="Period" value={whPeriod} onChange={(e) => setWhPeriod(e.target.value)} sx={{ minWidth: 200 }}>
                {REPORT_PERIODS.filter((p) => p.value !== 'custom').map((p) => <MenuItem key={p.value} value={p.value}>{p.label}</MenuItem>)}
              </TextField>
              <Typography variant="caption" color="text.secondary">
                Warnings are recorded on each scope&apos;s own snapshots — an enterprise average can be green while one facility burns red, so pick the site, department or NWT you want to look at.
              </Typography>
            </Stack>
            <WarningHistory snapshots={whSnapshots} scopeLabel={whScopeLabel} periodLabel={(REPORT_PERIODS.find((p) => p.value === whPeriod) || {}).label} />
          </Box>
        )}
      </TabPanel>
    </Box>
  );
}

export default REFReports;
